import React, { ChangeEvent, FC, useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import moment from 'moment';
import { get } from 'lodash';
import SahamDrawer from 'components/SahamDrawer';
import { arrayTranslate, translate } from 'containers/services/translate';
import messages from 'containers/ProvisionalClaimsPage/messges';
import {
  EMPTY_PROVISIONAL_CLAIM,
  PROVISIONAL_CLAIM,
} from 'containers/data-structure';
import {
  ContainerInDrawer,
  FormContainer,
} from 'containers/ProvisionalClaimsPage/styled-components';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import { Autocomplete } from '@material-ui/lab';
import { validationCourtFileNumber } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/utils';
import { filterStackHolder } from 'services/utils';
import validate, { displayErrorText } from 'containers/services/validateForm';
import { validateFormFields } from 'containers/services/validateForm';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import { DATE_FORMAT } from 'constants/global';
import { useDispatch } from 'react-redux';
import { requestAddProvisionalClaim } from 'containers/ProvisionalClaimsPage/actions';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { PROFIL_OPENNER_GSJ } from 'containers/RecapPage/canstants';

export interface AddProvisionalClaimDrawerProps {
  intl: IntlShape;
  handleToggleDrawer: Function;
  listings: any;
  lawyers: any;
  isDrawerOpen: boolean;
}

const AddProvisionalClaimDrawer: FC<AddProvisionalClaimDrawerProps> = (
  props,
): JSX.Element => {
  const constraints = {
    [PROVISIONAL_CLAIM.PROCEDURE_NATURE_ID]: {
      presence: true,
    },
    [PROVISIONAL_CLAIM.JURISDICTION_DEGREE_ID]: {
      presence: true,
    },
    [PROVISIONAL_CLAIM.COURT_NUMBER]: {
      presence: true,
    },
    [PROVISIONAL_CLAIM.JURISDICTION_ID]: {
      presence: true,
    },
    [PROVISIONAL_CLAIM.HEARING_DATE]: {
      presence: true,
    },
    [PROVISIONAL_CLAIM.LAWYER_CODE]: {
      presence: true,
    },
  };
  const dispatch = useDispatch();
  const [provisionalClaim, setProvisionalClaim] = useState(
    EMPTY_PROVISIONAL_CLAIM,
  );
  const [lawyerByJurisdiction, setLawyerByJurisdiction] = useState([]);
  const [formValidator, setFormValidator] = useState<any>({});
  const stackHolderFiltered = filterStackHolder(props.lawyers);
  const handleChange = (field: any) => (
    event: ChangeEvent<{ name?: string | undefined; value: any }>,
  ) => {
    if (field === PROVISIONAL_CLAIM.JURISDICTION_ID) {
      setProvisionalClaim({
        ...provisionalClaim,
        [field]: event.target.value,
      });
      const filterLawyerCodesFromMapping = new Set(
        get(props.listings, 'mappingLawyerJurisdiction', [])
          .filter((el: any) => el.jurisdictionId === event.target.value)
          .map((el: any) => el.lawyerCode),
      );

      const lawyersFiltered = stackHolderFiltered.filter((item: any) =>
        filterLawyerCodesFromMapping.has(item.ItemKey),
      );

      return setLawyerByJurisdiction(lawyersFiltered);
    }
    return setProvisionalClaim({
      ...provisionalClaim,
      [field]: event.target.value,
    });
  };
  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);
  const { message } = displayErrorText(
    formValidator,
    translateArrayErrorMessages(formValidator, true),
  );
  const handleChangeHearingDate = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    const hearingDate = moment(theDate as Date).format(DATE_FORMAT);
    setProvisionalClaim({
      ...provisionalClaim,
      [PROVISIONAL_CLAIM.HEARING_DATE]: hearingDate as any,
    });
  };
  const handleLawyerChange = (event: any, newValue: any | null) => {
    setProvisionalClaim({
      ...provisionalClaim,
      [PROVISIONAL_CLAIM.LAWYER_CODE]: newValue.ItemKey,
    });
  };
  const handleValidate = () => {
    const validator = validate(provisionalClaim, constraints);
    setFormValidator(validator);
    if (
      validateFormFields(validator) &&
      validationCourtFileNumber(
        get(provisionalClaim, [PROVISIONAL_CLAIM.COURT_NUMBER]),
      )
    ) {
      dispatch(
        requestAddProvisionalClaim(
          {
            payload: {
              provisionalClaim: {
                ...provisionalClaim,
                profilOpenner: PROFIL_OPENNER_GSJ,
              },
              isTrackingActive: featureService.hasFeature(
                FEATURES.TRACKING_ACTIONS_USER,
              ),
              isLawyerInboxesActive: featureService.hasFeature(
                FEATURES.LAWYER_INBOXES,
              ),
            },
          },
          successCallBack,
        ),
      );
    }
  };
  const successCallBack = () => {
    props.handleToggleDrawer();
    setProvisionalClaim(EMPTY_PROVISIONAL_CLAIM);
  };

  const filterListingProperties = (property: any) => {
    return get(props.listings, property, [])
      .filter(
        (element: { id: number; title: string; isVisible: boolean }) =>
          element.isVisible,
      )
      .map((element: { id: number; title: string; isVisible: boolean }) => (
        <MenuItem value={element.id}>{element.title}</MenuItem>
      ));
  };
  return (
    <SahamDrawer
      open={props.isDrawerOpen}
      handleToggleDrawer={props.handleToggleDrawer}
      title={translate(
        { ...messages.monitoringProvisionalClaimAddDrawerTitle },
        props,
      )}
      handleValidateDrawer={handleValidate}
      confirmButtonTitle={translate(
        messages.monitoringProvisionalClaimAddDrawerButtonTitle,
        props,
      )}
    >
      <ContainerInDrawer>
        <FormContainer>
          <div className="subContainer">
            <FormControl>
              <InputLabel>
                {translate(
                  messages.monitoringProvisionalClaimAddDrawerProcedureLabel,
                  props,
                )}
              </InputLabel>
              <Select
                className="input"
                onChange={handleChange(PROVISIONAL_CLAIM.PROCEDURE_NATURE_ID)}
                name={PROVISIONAL_CLAIM.PROCEDURE_NATURE_ID}
                value={get(
                  provisionalClaim,
                  [PROVISIONAL_CLAIM.PROCEDURE_NATURE_ID],
                  null,
                )}
              >
                {filterListingProperties('procedureNature')}
              </Select>
              {!!get(formValidator, PROVISIONAL_CLAIM.PROCEDURE_NATURE_ID) && (
                <FormHelperText className="error">
                  {message[PROVISIONAL_CLAIM.PROCEDURE_NATURE_ID]}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl>
              <InputLabel>
                {translate(
                  messages.monitoringProvisionalClaimAddDrawerDegreeJurisdiction,
                  props,
                )}
              </InputLabel>
              <Select
                className="input"
                onChange={handleChange(
                  PROVISIONAL_CLAIM.JURISDICTION_DEGREE_ID,
                )}
                name={PROVISIONAL_CLAIM.JURISDICTION_DEGREE_ID}
                value={get(
                  provisionalClaim,
                  [PROVISIONAL_CLAIM.JURISDICTION_DEGREE_ID],
                  null,
                )}
              >
                {filterListingProperties('jurisdictionDegrees')}
              </Select>
              {!!get(
                formValidator,
                PROVISIONAL_CLAIM.JURISDICTION_DEGREE_ID,
              ) && (
                <FormHelperText className="error">
                  {message[PROVISIONAL_CLAIM.JURISDICTION_DEGREE_ID]}
                </FormHelperText>
              )}
            </FormControl>
            <TextField
              label={translate(
                messages.monitoringProvisionalClaimAddDrawerCourtNumber,
                props,
              )}
              className="input"
              name={PROVISIONAL_CLAIM.COURT_NUMBER}
              onChange={handleChange(PROVISIONAL_CLAIM.COURT_NUMBER)}
              value={get(
                provisionalClaim,
                [PROVISIONAL_CLAIM.COURT_NUMBER],
                '',
              )}
              placeholder={'NNNNNN/NNNN/NNNN'}
              error={
                !!get(formValidator, PROVISIONAL_CLAIM.COURT_NUMBER) ||
                !validationCourtFileNumber(
                  get(provisionalClaim, [PROVISIONAL_CLAIM.COURT_NUMBER]),
                )
              }
              helperText={
                get(formValidator, PROVISIONAL_CLAIM.COURT_NUMBER)
                  ? message[PROVISIONAL_CLAIM.COURT_NUMBER]
                  : !validationCourtFileNumber(
                      get(provisionalClaim, [PROVISIONAL_CLAIM.COURT_NUMBER]),
                    )
                  ? translate(messages.courtFileNumberError, props)
                  : ''
              }
            />
            <FormControl>
              <InputLabel>
                {translate(
                  messages.monitoringProvisionalClaimAddDrawerJurisdiction,
                  props,
                )}
              </InputLabel>
              <Select
                className="input"
                onChange={handleChange(PROVISIONAL_CLAIM.JURISDICTION_ID)}
                name={PROVISIONAL_CLAIM.JURISDICTION_ID}
                value={get(
                  provisionalClaim,
                  [PROVISIONAL_CLAIM.JURISDICTION_ID],
                  null,
                )}
              >
                {filterListingProperties('jurisdiction')}
              </Select>
              {!!get(formValidator, PROVISIONAL_CLAIM.JURISDICTION_ID) && (
                <FormHelperText className="error">
                  {message[PROVISIONAL_CLAIM.JURISDICTION_ID]}
                </FormHelperText>
              )}
            </FormControl>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
              <KeyboardDatePicker
                className="input"
                autoOk
                variant="inline"
                invalidDateMessage={''}
                format="dd/MM/yyyy"
                label={translate(
                  {
                    ...messages.monitoringProvisionalClaimAddDrawerHearingDate,
                  },
                  props,
                )}
                name={PROVISIONAL_CLAIM.HEARING_DATE}
                value={dateStringToLocalDate(
                  get(provisionalClaim, [PROVISIONAL_CLAIM.HEARING_DATE]),
                )}
                onChange={handleChangeHearingDate}
                error={!!get(formValidator, PROVISIONAL_CLAIM.HEARING_DATE)}
                helperText={message[PROVISIONAL_CLAIM.HEARING_DATE]}
              />
            </MuiPickersUtilsProvider>
            <FormControl>
              <Autocomplete
                className="inpuoptionst"
                options={lawyerByJurisdiction}
                getOptionLabel={(option: any) => `${option.ItemDescription}`}
                onChange={handleLawyerChange}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={translate(
                      {
                        ...messages.monitoringProvisionalClaimAddDrawerLawyer,
                      },
                      props,
                    )}
                    error={!!get(formValidator, PROVISIONAL_CLAIM.LAWYER_CODE)}
                    helperText={message[PROVISIONAL_CLAIM.LAWYER_CODE]}
                  />
                )}
                renderOption={option => (
                  <Box display="flex" flexDirection="column">
                    {option.ItemDescription}
                  </Box>
                )}
                style={{ marginBottom: 16 }}
              />
            </FormControl>
          </div>
        </FormContainer>
      </ContainerInDrawer>
    </SahamDrawer>
  );
};

export default injectIntl(AddProvisionalClaimDrawer);
